import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { BadgePercent, Handshake, Rocket, Clock, Eye } from "lucide-react";

const collections = [
  { title: "Negocios rentables", icon: BadgePercent, key: "SaaS" },
  { title: "Empresas en traspaso", icon: Handshake, key: "Agencia" },
  { title: "Startups con potencial", icon: Rocket, key: "Startup" },
  { title: "Oportunidades recientes", icon: Clock, key: "Nuevo" },
  { title: "Más visitados", icon: Eye, key: "Popular" },
];

export default function Listings() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("Buscar");
  const [selectedCollection, setSelectedCollection] = useState("all");
  const [listings, setListings] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [scrollerItems, setScrollerItems] = useState([]);

  useEffect(() => {
    const fetchListings = async () => {
      const snapshot = await getDocs(collection(db, "listings"));
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setListings(items);

      // Agrupar por categoría
      const groupedListings = {};
      for (const item of items) {
        const category = item.category || "Otro";
        if (!groupedListings[category]) {
          groupedListings[category] = [];
        }
        groupedListings[category].push(item);
      }
      setGrouped(groupedListings);

      // Items para el scroller (3 por categoría popular)
      const scroll = Object.values(groupedListings)
        .flat()
        .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds)
        .slice(0, 15);
      setScrollerItems(scroll);
    };

    fetchListings();
  }, []);

  useEffect(() => {
    if (location.pathname === "/") {
      setActiveTab("Buscar");
    }
  }, [location.pathname]);

  const getCategoryStyle = (category) => {
    switch (category) {
      case "SaaS":
        return "bg-green-200 text-green-700";
      case "Agencia":
        return "bg-yellow-200 text-yellow-700";
      case "Startup":
        return "bg-blue-200 text-blue-700";
      case "Restaurante":
        return "bg-red-200 text-red-700";
      case "Bar":
        return "bg-purple-200 text-purple-700";
      case "Tienda":
        return "bg-pink-200 text-pink-700";
      case "Nuevo":
        return "bg-indigo-200 text-indigo-700";
      case "Popular":
        return "bg-orange-200 text-orange-700";
      case "Fintech":
        return "bg-blue-200 text-blue-700";
      default:
        return "bg-gray-200 text-gray-700";
    }
  };

  const listingsToShow =
    selectedCollection === "all"
      ? listings
      : listings.filter((l) => l.category?.toLowerCase() === selectedCollection.toLowerCase());

  const renderScroller = () => (
    <>
      <h2 className="text-md font-semibold text-gray-800 mb-2">Publicado recientemente</h2>
      <div className="relative overflow-hidden whitespace-nowrap bg-gray-50 py-3 mb-6">
        <div className="animate-marquee inline-flex space-x-4">
          {scrollerItems.concat(scrollerItems).map((item, idx) => (
            <Link
              to={`/oferta/${item.id}`}
              key={idx}
              className="inline-block bg-white border px-4 py-2 rounded-xl shadow-sm min-w-[240px] mr-4 hover:shadow-md transition"
            >
              <div className={`text-xs font-semibold mb-1 inline-block px-2 py-1 rounded-full ${getCategoryStyle(item.category)}`}>
                {item.category}
              </div>
              <div className="text-sm font-semibold text-gray-800">{item.title}</div>
              <div className="text-xs text-gray-500 truncate">{item.shortDescription}</div>
              <div className="text-xs font-semibold text-gray-700 mt-1">Precio: {item.askingPrice}</div>
            </Link>
          ))}
        </div>
        <style>{`
          @keyframes marquee {
            0%   { transform: translateX(0%); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee {
            animation: marquee 180s linear infinite;
          }
        `}</style>
      </div>
    </>
  );

  const renderCollectionGrid = () => (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
      {collections.map((item, idx) => (
        <div
          key={idx}
          className={`border rounded-xl py-6 px-4 flex flex-col items-center justify-center text-center bg-white hover:shadow-md transition cursor-pointer ${
            selectedCollection === item.key.toLowerCase() ? "ring-2 ring-yellow-500" : ""
          }`}
          onClick={() => setSelectedCollection(item.key.toLowerCase())}
        >
          <item.icon className="w-8 h-8 mb-2" style={{ color: "#f0b632" }} />
          <span className="text-sm font-medium text-gray-700">{item.title}</span>
        </div>
      ))}
    </div>
  );

  const renderListingCards = (listings) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {listings.map((item, idx) => (
        <Link to={`/oferta/${item.id}`} key={idx} className="border rounded-2xl p-6 bg-white hover:shadow-lg transition block">
          <div className="flex justify-between items-center mb-3">
            <div className={`text-xs font-semibold px-2 py-1 rounded-full ${getCategoryStyle(item.category)}`}>
              {item.category}
            </div>
          </div>
          <h3 className="text-md font-semibold text-gray-800 mb-1">{item.title}</h3>
          <p className="text-sm text-gray-600 mb-4">{item.shortDescription}</p>
          <div className="text-gray-800 mt-2">
  <p className="text-lg font-bold text-yellow-600">
    {typeof item.askingPrice === "number"
      ? new Intl.NumberFormat("es-ES", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(item.askingPrice)
      : item.askingPrice}
  </p>
  <div className="text-sm text-gray-600 mt-1">
    <p><strong>Ingresos 12m:</strong> {item.ttmRevenue}</p>
    <p><strong>Beneficio 12m:</strong> {item.ttmProfit}</p>
  </div>
</div>

        </Link>
      ))}
    </div>
  );

  return (
    <div>
      {renderScroller()}

      <div className="flex gap-8 border-b mb-6">
        <button
          onClick={() => {
            setActiveTab("Buscar");
            setSelectedCollection("all");
          }}
          className={`pb-2 text-sm ${activeTab === "Buscar" ? "text-yellow-700 border-b-2 border-yellow-700" : "text-gray-500 hover:text-blue-600"}`}
        >
          Buscar
        </button>
        <button
          onClick={() => {
            setActiveTab("AllListings");
            setSelectedCollection("all");
          }}
          className={`pb-2 text-sm ${activeTab === "AllListings" ? "text-yellow-700 border-b-2 border-yellow-700" : "text-gray-500 hover:text-blue-600"}`}
        >
          Listado
        </button>
      </div>

      {activeTab === "Buscar" && (
        <>
          <h2 className="text-md font-semibold text-gray-800 mb-4">Colecciones</h2>
          {renderCollectionGrid()}
          {selectedCollection !== "all" && (
            <button
              onClick={() => setSelectedCollection("all")}
              className="mb-4 text-sm text-blue-600 hover:underline"
            >
              ← Ver todos los negocios
            </button>
          )}

          {renderListingCards(listingsToShow)}
        </>
      )}

      {activeTab === "AllListings" && (
        <>
          <h2 className="text-md font-semibold text-gray-800 mb-4">Todos los listados</h2>
          {renderListingCards(listingsToShow)}
        </>
      )}
    </div>
  );
}
